import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, Empty, OrigenBadge, EstadoBadge } from "@/components/common";
import { ArrowLeft, CopyCheck, Users, GitMerge, Split, AlertTriangle, CheckCircle2 } from "lucide-react";

const btnPri = "flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-vino-800 disabled:opacity-50";
const btnSec = "flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50";

function FichaGrupo({ g, esAdmin, onHecho }) {
  const [principal, setPrincipal] = useState(g.personas[0].id);
  const [nombre, setNombre] = useState(g.personas[0].nombre_completo || "");
  const [busy, setBusy] = useState(false);
  const unificar = async () => {
    if (!window.confirm("¿Unificar estas fichas en una sola? Las vinculaciones, expedientes y accesos pasan a la ficha principal. Nada se elimina.")) return;
    setBusy(true);
    try {
      await api.post("/duplicados/fichas/unificar", { principal_id: principal, otras_ids: g.personas.map((p) => p.id), nombre_completo: nombre });
      toast.success("Fichas unificadas en un solo registro");
      onHecho();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const nombres = [...new Set(g.personas.map((p) => p.nombre_completo).filter(Boolean))];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ficha-grupo-${g.documento}`}>
      <p className="font-semibold text-slate-800">Documento {g.documento} · {g.personas.length} fichas personales</p>
      {g.nombres_distintos && <p className="mt-1 flex items-center gap-1.5 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Los nombres no coinciden: elige el correcto antes de unificar.</p>}
      <div className="mt-3 space-y-2">
        {g.personas.map((p) => (
          <label key={p.id} className={`block cursor-pointer rounded-lg border p-3 text-sm ${principal === p.id ? "border-vino-400 bg-vino-50/50" : "border-slate-200"}`}>
            <span className="flex items-center gap-2"><input type="radio" checked={principal === p.id} onChange={() => setPrincipal(p.id)} disabled={!esAdmin} data-testid={`ficha-principal-${p.id}`} /><span className="font-medium">{p.nombre_completo}</span><span className="text-xs text-slate-500">· origen {p.origen}</span></span>
            <span className="mt-1 block pl-5 text-xs text-slate-500">{p.vinculaciones.length} vinculación(es): {p.vinculaciones.map((v) => `${v.empresa_cliente} (${v.cargo || "sin cargo"})`).join(" · ") || "—"}{p.expedientes.length > 0 && ` · cuentas: ${p.expedientes.map((e) => e.email).join(", ")}`}</span>
          </label>
        ))}
      </div>
      {esAdmin && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="min-w-0 flex-1 text-xs text-slate-600">Nombre que se conserva
            <select value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid={`ficha-nombre-${g.documento}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">{nombres.map((n) => <option key={n}>{n}</option>)}</select>
          </label>
          <button onClick={unificar} disabled={busy} data-testid={`ficha-unificar-${g.documento}`} className={btnPri}>{busy ? <Spinner className="h-4 w-4" /> : <GitMerge className="h-4 w-4" />} Unificar fichas</button>
        </div>
      )}
    </div>
  );
}

function VincResumen({ v, titulo }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-sm">
      <p className="text-[11px] font-semibold uppercase text-slate-400">{titulo}</p>
      <p className="font-medium text-slate-800">{v.empresa_cliente} · {v.cargo || "sin cargo"}</p>
      <p className="text-xs text-slate-500">{v.nombre_completo} · ingreso {v.fecha_ingreso || "—"}</p>
      <div className="mt-1 flex gap-1.5"><OrigenBadge origen={v.origen} /><EstadoBadge estado={v.estado} /></div>
    </div>
  );
}

function ParVinculaciones({ p, esAdmin, onHecho }) {
  const [elec, setElec] = useState(() => Object.fromEntries(p.diferencias.map((d) => [d.idx, d.principal ? "principal" : "secundaria"])));
  const [busy, setBusy] = useState(false);
  const accion = async (tipo) => {
    const msg = tipo === "unificar" ? "¿Es el mismo contrato? Quedará una sola vinculación con los datos elegidos; la otra se archiva con su historial." : "¿Son vinculaciones distintas? Se conservan ambas en el historial de la persona.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      if (tipo === "unificar") await api.post("/duplicados/vinculaciones/unificar", { principal_id: p.principal.id, secundaria_id: p.secundaria.id, elecciones: elec });
      else await api.post("/duplicados/vinculaciones/distintas", { a: p.principal.id, b: p.secundaria.id });
      toast.success(tipo === "unificar" ? "Vinculaciones unificadas" : "Marcadas como vinculaciones distintas");
      onHecho();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`par-${p.principal.id}`}>
      <p className="font-semibold text-slate-800">{p.principal.nombre_completo} · {p.documento}</p>
      <p className="text-xs text-slate-500">Dos vinculaciones con la misma fecha de ingreso. Decide si son el mismo contrato.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2"><VincResumen v={p.principal} titulo="Registro A" /><VincResumen v={p.secundaria} titulo="Registro B" /></div>
      {p.diferencias.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-lg border border-slate-100">
          <table className="w-full min-w-[520px] text-xs">
            <thead><tr className="bg-slate-50 text-left text-slate-500"><th className="px-2 py-1.5">Campo</th><th className="px-2 py-1.5">Registro A</th><th className="px-2 py-1.5">Registro B</th></tr></thead>
            <tbody>{p.diferencias.map((d) => (
              <tr key={d.idx} className="border-t border-slate-100">
                <td className="px-2 py-1.5 font-medium text-slate-700">{d.campo}</td>
                {["principal", "secundaria"].map((k) => (
                  <td key={k} className="px-2 py-1.5"><label className="flex items-center gap-1.5"><input type="radio" name={`${p.principal.id}-${d.idx}`} checked={elec[d.idx] === k} disabled={!esAdmin} onChange={() => setElec((e) => ({ ...e, [d.idx]: k }))} data-testid={`par-eleccion-${d.idx}-${k}`} />{d[k] || <span className="text-slate-300">(vacío)</span>}</label></td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {esAdmin && (
        <div className="mt-3 flex flex-wrap justify-end gap-2">
          <button onClick={() => accion("distintas")} disabled={busy} data-testid={`par-distintas-${p.principal.id}`} className={btnSec}><Split className="h-4 w-4" /> Son vinculaciones distintas</button>
          <button onClick={() => accion("unificar")} disabled={busy} data-testid={`par-unificar-${p.principal.id}`} className={btnPri}><GitMerge className="h-4 w-4" /> Es el mismo contrato: unificar</button>
        </div>
      )}
    </div>
  );
}

function Alerta({ a, esAdmin, onHecho }) {
  const revisar = () => api.post(`/duplicados/alertas/${a.id}/revisada`).then(() => { toast.success("Alerta revisada"); onHecho(); }).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm" data-testid={`alerta-${a.id}`}>
      <p className="text-amber-900">Documento <strong>{a.cedula}</strong>: registrado como <strong>{a.nombre_registrado}</strong>, llegó como <strong>{a.nombre_nuevo}</strong> ({a.origen}). No se creó otra persona.</p>
      {esAdmin && <button onClick={revisar} data-testid={`alerta-revisada-${a.id}`} className={btnSec}><CheckCircle2 className="h-4 w-4" /> Marcar revisada</button>}
    </div>
  );
}

export default function Duplicados() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const esAdmin = user.role === "admin";
  const [d, setD] = useState(null);
  const load = () => api.get("/duplicados").then((r) => setD(r.data)).catch(() => setD({ fichas: [], pares: [], alertas: [] }));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="flex h-48 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;
  const vacio = !d.fichas.length && !d.pares.length && !d.alertas.length;
  return (
    <div className="space-y-6" data-testid="duplicados-page">
      <button onClick={() => navigate("/empleados")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-vino-700" data-testid="duplicados-volver"><ArrowLeft className="h-4 w-4" /> Volver a Empleados</button>
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Revisar duplicados</h1>
        <p className="mt-1 text-sm text-slate-500">Cada trabajador se identifica por su número de documento. {esAdmin ? "Tú decides qué unificar: nada se elimina y los documentos firmados no se modifican." : "Solo un administrador puede unificar."}</p>
      </div>
      {vacio && <Empty icon={CopyCheck} title="Sin duplicados pendientes" subtitle="Cada persona tiene una sola ficha en Roge." />}
      {d.alertas.length > 0 && <section className="space-y-2"><h2 className="text-base font-semibold text-slate-800 md:text-lg">Mismo documento con otro nombre ({d.alertas.length})</h2>{d.alertas.map((a) => <Alerta key={a.id} a={a} esAdmin={esAdmin} onHecho={load} />)}</section>}
      {d.fichas.length > 0 && <section className="space-y-3"><h2 className="flex items-center gap-2 text-base font-semibold text-slate-800 md:text-lg"><Users className="h-4 w-4" /> Fichas personales duplicadas ({d.fichas.length})</h2>{d.fichas.map((g) => <FichaGrupo key={g.documento} g={g} esAdmin={esAdmin} onHecho={load} />)}</section>}
      {d.pares.length > 0 && <section className="space-y-3"><h2 className="text-base font-semibold text-slate-800 md:text-lg">Vinculaciones con la misma fecha de ingreso ({d.pares.length})</h2>{d.pares.map((p) => <ParVinculaciones key={`${p.principal.id}-${p.secundaria.id}`} p={p} esAdmin={esAdmin} onHecho={load} />)}</section>}
    </div>
  );
}
